import { useState, useEffect } from 'react';
import { D, st, apiGet, apiPost } from '../constants';

export default function InvitePage({ token }) {
  const [step, setStep] = useState('loading');
  const [info, setInfo] = useState(null);
  const [pin, setPin] = useState('');
  const [pinConfirm, setPinConfirm] = useState('');
  const [installPrompt, setInstallPrompt] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches || !!window.navigator.standalone;

  useEffect(() => {
    const handler = e => { e.preventDefault(); setInstallPrompt(e); };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  useEffect(() => {
    apiGet(`/employee/invite/${token}`)
      .then(data => {
        if (data?.error) { setErr(data.error); setStep('error'); return; }
        setInfo(data);
        setStep(isStandalone ? 'pin' : 'install');
      })
      .catch(() => { setErr('Invite link is invalid or has expired.'); setStep('error'); });
  }, [token]);

  const handleInstall = async () => {
    if (installPrompt) { await installPrompt.prompt(); setInstallPrompt(null); }
    setStep('pin');
  };

  const handleSetPin = async () => {
    setErr('');
    if (!/^\d{4}$/.test(pin)) { setErr('PIN must be exactly 4 digits'); return; }
    if (pin !== pinConfirm) { setErr('PINs do not match'); return; }
    setSaving(true);
    try {
      const result = await apiPost(`/employee/invite/${token}/setup`, { pin });
      if (result?.error) { setErr(result.error); setSaving(false); return; }
      localStorage.setItem('eb:employee', JSON.stringify({
        token: result.token, userId: result.userId,
        companyId: result.companyId, name: result.name, role: result.role,
      }));
      setStep('done');
    } catch(e) {
      setErr('Something went wrong. Please try again.');
      setSaving(false);
    }
  };

  const wrap = child => (
    <div style={{minHeight:"100vh",background:D.surface,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
      <div style={{width:"100%",maxWidth:420}}>
        <div style={{textAlign:"center",marginBottom:32}}>
          <div style={{fontSize:36,fontWeight:800,color:D.accent}}>⚡ AmpBooks</div>
        </div>
        {child}
      </div>
    </div>
  );

  if (step === 'loading') return wrap(
    <div style={{textAlign:"center",color:D.muted,padding:40}}>Checking invite link…</div>
  );

  if (step === 'error') return wrap(
    <div style={{...st.card,textAlign:"center"}}>
      <div style={{fontSize:40,marginBottom:12}}>❌</div>
      <div style={{fontWeight:700,fontSize:18,marginBottom:8,color:D.danger}}>Link Invalid</div>
      <div style={{color:D.muted,fontSize:14,marginBottom:12}}>{err}</div>
      <div style={{color:D.muted,fontSize:13}}>Ask your employer to send a new invite.</div>
    </div>
  );

  if (step === 'install') return wrap(
    <div style={st.card}>
      <div style={{textAlign:"center",marginBottom:24}}>
        <div style={{fontSize:28,marginBottom:10}}>👋</div>
        <div style={{fontWeight:700,fontSize:20,marginBottom:6}}>Welcome, {info?.name}!</div>
        <div style={{color:D.muted,fontSize:14}}>You've been added to <strong style={{color:D.text}}>{info?.companyName}</strong>.</div>
      </div>

      {!isIOS && installPrompt && (
        <div style={{marginBottom:20}}>
          <div style={{fontSize:13,color:D.muted,marginBottom:10,textAlign:"center"}}>Install the app for one-tap access each day:</div>
          <button style={{...st.btn("primary"),width:"100%",marginLeft:0,padding:"14px 0",fontSize:15}} onClick={handleInstall}>
            📲 Install AmpBooks App
          </button>
          <button style={{...st.btn(),width:"100%",marginLeft:0,marginTop:8,fontSize:13}} onClick={() => setStep('pin')}>
            Skip — continue in browser
          </button>
        </div>
      )}

      {isIOS && !isStandalone && (
        <div>
          <div style={{background:"#1a1a2e",border:`1px solid ${D.border}`,borderRadius:10,padding:16,marginBottom:16}}>
            <div style={{fontWeight:600,fontSize:13,marginBottom:10}}>📲 Add to your Home Screen:</div>
            <div style={{fontSize:13,color:D.muted,lineHeight:2}}>
              <div>1. Tap the <strong style={{color:D.text}}>Share button</strong> at the bottom of Safari</div>
              <div>2. Tap <strong style={{color:D.text}}>"Add to Home Screen"</strong></div>
              <div>3. Tap <strong style={{color:D.text}}>"Add"</strong> in the top right</div>
            </div>
          </div>
          <button style={{...st.btn("primary"),width:"100%",marginLeft:0,padding:"14px 0",fontSize:15}} onClick={() => setStep('pin')}>
            Continue →
          </button>
        </div>
      )}

      {!installPrompt && !isIOS && (
        <button style={{...st.btn("primary"),width:"100%",marginLeft:0,padding:"14px 0",fontSize:15}} onClick={() => setStep('pin')}>
          Continue →
        </button>
      )}
    </div>
  );

  if (step === 'pin') return wrap(
    <div style={st.card}>
      <div style={{textAlign:"center",marginBottom:24}}>
        <div style={{fontSize:28,marginBottom:10}}>🔐</div>
        <div style={{fontWeight:700,fontSize:20,marginBottom:6}}>Create Your PIN</div>
        <div style={{color:D.muted,fontSize:14}}>You'll use this 4-digit PIN to clock in each day.</div>
      </div>
      <div style={{marginBottom:14}}>
        <label style={{fontSize:13,color:D.muted,display:"block",marginBottom:6}}>Create PIN</label>
        <input style={{...st.input,fontSize:24,textAlign:"center",letterSpacing:10}} type="password"
          inputMode="numeric" pattern="[0-9]*" maxLength={4} value={pin} autoFocus
          onChange={e => setPin(e.target.value.replace(/\D/g,'').slice(0,4))} placeholder="••••"/>
      </div>
      <div style={{marginBottom:20}}>
        <label style={{fontSize:13,color:D.muted,display:"block",marginBottom:6}}>Confirm PIN</label>
        <input style={{...st.input,fontSize:24,textAlign:"center",letterSpacing:10}} type="password"
          inputMode="numeric" pattern="[0-9]*" maxLength={4} value={pinConfirm}
          onChange={e => setPinConfirm(e.target.value.replace(/\D/g,'').slice(0,4))}
          placeholder="••••" onKeyDown={e => e.key==='Enter' && handleSetPin()}/>
      </div>
      {err && <div style={{color:D.danger,fontSize:13,marginBottom:12,textAlign:"center"}}>{err}</div>}
      <button style={{...st.btn("primary"),width:"100%",marginLeft:0,padding:"14px 0",fontSize:15}}
        onClick={handleSetPin} disabled={saving || pin.length !== 4}>
        {saving ? "Setting up…" : "Set My PIN →"}
      </button>
    </div>
  );

  if (step === 'done') return wrap(
    <div style={{...st.card,textAlign:"center"}}>
      <div style={{fontSize:48,marginBottom:16}}>✅</div>
      <div style={{fontWeight:700,fontSize:22,marginBottom:8}}>You're all set, {info?.name}!</div>
      <div style={{color:D.muted,fontSize:14,marginBottom:24,lineHeight:1.6}}>Your PIN is saved. Open the app each morning to clock in for the day.</div>
      <button style={{...st.btn("primary"),width:"100%",marginLeft:0,padding:"14px 0",fontSize:15}}
        onClick={() => window.location.href = '/'}>
        Open Time Clock →
      </button>
    </div>
  );
}
